// חוקר סדרות (11, 2026-10-13): גרף + טבלה שהיא המקרא — לגרף עם סדרות רבות (קבוצה × חודש). DOM בלבד, בלי globals:
// echarts, tokens ופורמט מגיעים כארגומנטים (deps), כך שאותו קוד רץ בדף המארח (ChartBlock) וב-iframe של בלוק app (ab.explorer).
// ה-import למטה מוסר כשהקובץ מוזרק ל-iframe (שם series.js באותו scope). טיפוסים: explorer.d.ts.
import { MUTED_DARK, MUTED_LIGHT, cartesianOption, initialVisible, missingColumnsHe, normalize, rankSeries, seriesModel, seriesStats, seriesStyles } from './series.js';

export const STAT_HE = { total: 'סה״כ', share: 'נתח', avg: 'ממוצע', last: 'אחרון', change: 'שינוי' };
export const EXPLORER_MOBILE_ROWS = 8;

/**
 * createExplorer(el, opts, deps) → {chart, setRows, show, only, all, none, top, highlight, state, destroy}.
 * opts: {rows, x, y, seriesBy?, chartType?: 'line'|'bar'|'area', stacked?, unit?, top?, show?, highlight?, marks?,
 *        series?: [{key, labelHe?, color?, dash?}], stats?, titleHe?, seriesLabelHe?, height?, time?, onChange?(state)}
 * deps: {echarts, t: {palette, fg, muted, border, grid, card, font?, dark?}, fmt(value, type), axisFmt(n)}
 */
export function createExplorer(el, opts, deps) {
  const { echarts, t, fmt, axisFmt } = deps;
  const doc = el.ownerDocument;
  const win = doc.defaultView;
  const h = (tag, cls, text) => {
    const e = doc.createElement(tag);
    if (cls) e.className = cls;
    if (text !== undefined) e.textContent = text;
    return e;
  };
  const unit = opts.unit || 'number';
  const kind = opts.chartType === 'bar' || opts.chartType === 'area' ? opts.chartType : 'line';
  const stats = opts.stats && opts.stats.length ? opts.stats : ['total', 'share'];
  const ys = Array.isArray(opts.y) ? opts.y : [opts.y];
  const root = h('div', 'abx');
  root.dataset.explorer = '1';
  el.appendChild(root);

  const noop = () => {};
  const missing = missingColumnsHe(opts.rows || [], opts.seriesBy ? [opts.x, opts.seriesBy, ys[0]] : [opts.x, ...ys]);
  if (missing) {
    const err = h('div', 'ab-error abx-error', missing);
    err.setAttribute('role', 'alert');
    root.appendChild(err);
    return { el: root, chart: null, setRows: noop, show: noop, only: noop, all: noop, none: noop, top: noop, highlight: noop, state: () => ({ visible: [], highlight: [] }), destroy: () => root.remove() };
  }

  let rows = opts.rows || [];
  let ranked = [];
  let styles = [];
  let keys = [];
  let grand = 0;
  let model = { xs: [], time: false, series: [] };
  let visible = new Set();
  let hl = (opts.highlight || []).map(String);
  let query = '';
  let expanded = false;
  let focus = null; // מפתח הסדרה שבפוקוס (מעבר על שורה או על קו)
  const nameOf = (k) => styles[keys.indexOf(k)]?.name ?? k;

  const compute = () => {
    model = seriesModel(rows, { x: opts.x, y: ys, seriesBy: opts.seriesBy, time: opts.time });
    ranked = rankSeries(model.series);
    keys = ranked.map((s) => s.name);
    // צבע לפי הדירוג (יציב כשמדליקים ומכבים), series[] של אייבי גובר
    styles = seriesStyles({ y: keys, series: opts.series }, t.palette, t.dark ? MUTED_DARK : MUTED_LIGHT);
    grand = ranked.reduce((s, r) => s + Math.abs(r.total), 0);
  };
  compute();
  visible = new Set(initialVisible(keys, { top: opts.top, show: opts.show }));

  // ---------- DOM ----------
  const chartEl = h('div', 'abx-chart');
  chartEl.setAttribute('role', 'img');
  chartEl.setAttribute('aria-label', opts.titleHe || 'גרף');
  if (opts.height) chartEl.style.height = `${opts.height}px`;
  const bar = h('div', 'abx-bar');
  const actions = h('div', 'abx-actions');
  actions.setAttribute('role', 'group');
  actions.setAttribute('aria-label', 'בחירת סדרות');
  const btn = (label, act, on) => {
    const b = h('button', 'abx-btn', label);
    b.type = 'button';
    b.dataset.act = act;
    b.addEventListener('click', on);
    actions.appendChild(b);
    return b;
  };
  const bAll = btn('הכל', 'all', () => api.all());
  const bNone = btn('נקה', 'none', () => api.none());
  const b5 = btn('5 המובילים', 'top5', () => api.top(5));
  const b10 = btn('10 המובילים', 'top10', () => api.top(10));
  bar.appendChild(actions);
  const search = h('input', 'abx-search');
  search.type = 'search';
  search.placeholder = 'חיפוש…';
  search.setAttribute('aria-label', 'חיפוש סדרה');
  search.addEventListener('input', () => {
    query = normalize(search.value);
    filter();
  });
  bar.appendChild(search);
  const hiddenNote = h('span', 'abx-hidden');
  hiddenNote.setAttribute('aria-live', 'polite');
  bar.appendChild(hiddenNote);
  const list = h('div', 'abx-list');
  const table = h('table', 'abx-table');
  list.appendChild(table);
  const more = h('button', 'abx-more abx-btn');
  more.type = 'button';
  more.addEventListener('click', () => {
    expanded = !expanded;
    filter();
  });
  root.append(chartEl, bar, list, more);

  const chart = echarts.init(chartEl, null, { renderer: 'svg' });
  const option = () => {
    const m = { xs: model.xs, time: model.time, series: ranked.map((s, i) => ({ name: styles[i].name, data: s.data, color: styles[i].color, dash: styles[i].dash })) };
    const selected = {};
    keys.forEach((k, i) => (selected[styles[i].name] = visible.has(k)));
    const o = cartesianOption(m, t, { kind, stacked: opts.stacked, fmt: (v) => fmt(v, unit), axisFmt, legend: false, selected, highlight: hl.filter((k) => keys.includes(k)).map(nameOf), marks: opts.marks, explorer: true, focus: focus !== null && visible.has(focus) ? nameOf(focus) : null });
    // 24 §1.1: בדף התצוגה של אייבי (html[data-ab-preview]) — בלי הנפשה, הצילום מיידי
    return doc.documentElement.hasAttribute('data-ab-preview') ? { ...o, animation: false } : o;
  };
  const draw = () => chart.setOption(option(), true);
  const setFocus = (k) => {
    if (focus === k) return;
    focus = k;
    chart.setOption(option());
  };

  const rowEls = new Map();
  // סכומים גדולים — בלי אגורות (הטבלה משמשת להשוואה, לא לחשבונאות)
  const round = (v) => (typeof v === 'number' && Math.abs(v) >= 1000 ? Math.round(v) : v);
  const statCell = (k, s, st) => {
    const td = h('td', `abx-num abx-${k}`);
    if (k === 'total') td.textContent = fmt(round(st.total), unit);
    else if (k === 'share') td.textContent = grand ? fmt(Math.abs(st.total) / grand, 'percent') : '–';
    else if (k === 'avg') td.textContent = fmt(round(st.avg), unit);
    else if (k === 'last') td.textContent = fmt(round(st.last), unit);
    else if (k === 'change') {
      if (st.change === null) td.textContent = '–';
      else {
        td.classList.add(st.change > 0 ? 'abx-up' : st.change < 0 ? 'abx-down' : 'abx-flat');
        const a = h('span', 'abx-arrow', st.change > 0 ? '▲' : st.change < 0 ? '▼' : '•');
        a.setAttribute('aria-hidden', 'true');
        const v = h('bdi', '', `${st.change > 0 ? '+' : st.change < 0 ? '−' : ''}${fmt(Math.abs(st.change), 'percent')}`);
        td.append(a, v);
      }
    }
    return td;
  };
  const renderTable = () => {
    table.textContent = '';
    rowEls.clear();
    const thead = table.createTHead();
    const hr = thead.insertRow();
    const th0 = h('th', 'abx-c');
    th0.appendChild(h('span', 'abx-sr', 'מוצג'));
    hr.appendChild(th0);
    hr.appendChild(h('th', 'abx-n', opts.seriesLabelHe || opts.seriesBy || 'סדרה'));
    for (const k of stats) hr.appendChild(h('th', 'abx-num', STAT_HE[k] || k));
    const tb = table.createTBody();
    ranked.forEach((s, i) => {
      const k = keys[i];
      const st = styles[i];
      const tr = tb.insertRow();
      tr.dataset.name = k;
      tr.dataset.rank = String(i + 1);
      const c = tr.insertCell();
      c.className = 'abx-c';
      const cb = h('input', 'abx-check');
      cb.type = 'checkbox';
      cb.setAttribute('aria-label', `הצג ${st.name}`);
      cb.addEventListener('change', () => {
        if (cb.checked) visible.add(k);
        else visible.delete(k);
        apply();
      });
      c.appendChild(cb);
      const n = tr.insertCell();
      n.className = 'abx-n';
      const sw = h('span', `abx-sw${st.dash !== 'solid' ? ' abx-sw-dash' : ''}`);
      sw.style.setProperty('--sw', st.color);
      sw.setAttribute('aria-hidden', 'true');
      const nb = h('button', 'abx-name');
      nb.type = 'button';
      nb.title = 'רק זה (לחיצה נוספת: הכל)';
      nb.appendChild(h('bdi', '', st.name));
      nb.addEventListener('click', () => (visible.size === 1 && visible.has(k) ? api.all() : api.only(k)));
      n.append(sw, nb);
      const sts = seriesStats(s.data);
      for (const sk of stats) tr.appendChild(statCell(sk, s, sts));
      const enter = () => {
        tr.classList.add('abx-hl');
        setFocus(k);
      };
      const leave = () => {
        tr.classList.remove('abx-hl');
        if (focus === k) setFocus(null);
      };
      tr.addEventListener('mouseenter', enter);
      tr.addEventListener('mouseleave', leave);
      tr.addEventListener('focusin', enter);
      tr.addEventListener('focusout', leave);
      rowEls.set(k, { tr, cb });
    });
  };

  const mq = win && win.matchMedia ? win.matchMedia('(max-width: 767.98px)') : null;
  const filter = () => {
    const limit = mq && mq.matches && !expanded && !query ? EXPLORER_MOBILE_ROWS : Infinity;
    let shown = 0;
    let matched = 0;
    for (const [k, r] of rowEls) {
      const match = !query || normalize(nameOf(k)).includes(query) || normalize(k).includes(query);
      if (match) matched++;
      const on = match && shown < limit;
      if (on) shown++;
      r.tr.hidden = !on;
    }
    const capped = mq && mq.matches && !query && keys.length > EXPLORER_MOBILE_ROWS;
    more.hidden = !capped;
    more.textContent = expanded ? 'הצג פחות' : `הצג הכל (${keys.length})`;
    root.dataset.matched = String(matched);
  };
  const sameSet = (arr) => arr.length === visible.size && arr.every((k) => visible.has(k));
  const apply = (redraw) => {
    if (redraw) draw();
    else {
      const selected = {};
      keys.forEach((k, i) => (selected[styles[i].name] = visible.has(k)));
      chart.setOption({ legend: { selected } });
    }
    for (const [k, r] of rowEls) {
      const on = visible.has(k);
      r.cb.checked = on;
      r.tr.classList.toggle('abx-off', !on);
      r.tr.classList.toggle('abx-key', hl.includes(k));
    }
    const off = keys.length - visible.size;
    hiddenNote.textContent = off > 0 ? `+${off} מוסתרים` : '';
    bAll.setAttribute('aria-pressed', String(off === 0));
    bNone.setAttribute('aria-pressed', String(visible.size === 0));
    b5.setAttribute('aria-pressed', String(sameSet(keys.slice(0, 5))));
    b10.setAttribute('aria-pressed', String(sameSet(keys.slice(0, 10))));
    b5.hidden = keys.length <= 5;
    b10.hidden = keys.length <= 10;
    search.hidden = keys.length <= 12;
    if (opts.onChange) opts.onChange(api.state());
  };

  // מעבר על קו → השורה שלו מסומנת
  const markRow = (name) => {
    for (const [k, r] of rowEls) r.tr.classList.toggle('abx-hover', name !== null && nameOf(k) === name);
  };
  const keyOfName = (name) => keys.find((k) => nameOf(k) === name) ?? null;
  chart.on('mouseover', (p) => {
    if (!p || p.componentType !== 'series' || !p.seriesName) return;
    markRow(p.seriesName);
    setFocus(keyOfName(p.seriesName));
  });
  const out = () => {
    markRow(null);
    setFocus(null);
  };
  chart.on('mouseout', out);
  chart.on('globalout', out);

  const RO = win && win.ResizeObserver;
  const ro = RO ? new RO(() => chart.resize()) : null;
  if (ro) ro.observe(chartEl);
  const onMq = () => filter();
  if (mq && mq.addEventListener) mq.addEventListener('change', onMq);

  const api = {
    el: root,
    chart,
    setRows(r) {
      rows = r || [];
      const prev = visible;
      compute();
      visible = new Set(keys.filter((k) => prev.has(k)));
      if (!visible.size) visible = new Set(initialVisible(keys, { top: opts.top, show: opts.show }));
      renderTable();
      filter();
      apply(true);
    },
    show(list) {
      const want = new Set((list || []).map(String));
      visible = new Set(keys.filter((k) => want.has(k)));
      apply();
    },
    only(k) {
      api.show([k]);
    },
    all() {
      visible = new Set(keys);
      apply();
    },
    none() {
      visible = new Set();
      apply();
    },
    top(n) {
      visible = new Set(keys.slice(0, Math.max(1, n)));
      apply();
    },
    highlight(list) {
      hl = (list || []).map(String);
      apply(true);
    },
    state() {
      return { visible: keys.filter((k) => visible.has(k)), highlight: hl.slice() };
    },
    destroy() {
      if (ro) ro.disconnect();
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onMq);
      chart.dispose();
      root.remove();
    },
  };
  renderTable();
  filter();
  apply(true);
  return api;
}
